import { useTranslation } from "@opendash/core";
import type { InputRef } from "antd";
import { Drawer, Input, Select, Table, Typography } from "antd";
import type { ColumnType, TableProps } from "antd/es/table";
import { ColumnFilterItem } from "antd/es/table/interface";
import React, { useRef, useState } from "react";

interface DetailDrawerDataItem {
  [key: string]: any;
}

export interface DetailDrawerProps
  extends Omit<React.ComponentProps<typeof Drawer>, "children"> {
  data?: DetailDrawerDataItem[];
  columns?: (
    defaultCols: TableProps<DetailDrawerDataItem>["columns"]
  ) => TableProps<DetailDrawerDataItem>["columns"];
}

export const DataDetailDrawer = (props: DetailDrawerProps) => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);
  const t = useTranslation();
  // Handler for initiating a search
  const handleSearch = (
    selectedKeys: string[],
    confirm: () => void,
    dataIndex: string
  ) => {
    confirm(); // Confirms the filter action
    setSearchText(selectedKeys[0]); // Sets the current search text
    setSearchedColumn(dataIndex); // Identifies the column being searched
  };

  // Handler for resetting a search filter
  const handleReset = (clearFilters: () => void) => {
    clearFilters(); // Clears all filters for the column
    setSearchText(""); // Resets search text
  };

  // Infers the data type and generates sorters, filters, and search props for a column
  const getColumnTypeAndFeatures = (
    key: string,
    sampleValue: {
      type: "String" | "Number" | "Boolean" | "Object" | "Geo";
      value: any;
    }
  ): Partial<ColumnType<DetailDrawerDataItem>> => {
    const type = sampleValue?.type;
    console.log(
      `getColumnTypeAndFeatures: key=${key}, type=${type}, sampleValue=`,
      sampleValue
    );
    let sorter: ColumnType<DetailDrawerDataItem>["sorter"];
    let filters: ColumnFilterItem[] | undefined;
    let searchProps: Partial<ColumnType<DetailDrawerDataItem>> = {};
    let onFilter: ColumnType<DetailDrawerDataItem>["onFilter"];

    switch (type) {
      case "Number":
        sorter = (a, b) => {
          const valA = a[key]?.value;
          const valB = b[key]?.value;
          return (valA ?? 0) - (valB ?? 0); // Handle null/undefined values
        };
        // For numbers, custom range filters might be needed, not simple 'filters' array

        break;
      case "Boolean":
        sorter = (a, b) => {
          const valA = a[key]?.value;
          const valB = b[key]?.value;
          return valA === valB ? 0 : valA ? -1 : 1; // False comes before True
        };
        filters = [
          { text: "True", value: true },
          { text: "False", value: false },
        ];
        onFilter = (value, record) => record[key]?.value === value;
        break;
      case "String":
        {
          sorter = (a, b) => {
            const valA = String(a[key]?.value || "").toLowerCase();
            const valB = String(b[key]?.value || "").toLowerCase();
            return valA.localeCompare(valB);
          };

          // Generate filters for string columns if unique values are few (e.g., for categories)
          const uniqueValues = Array.from(
            new Set(
              props.data?.map((item) => item[key]?.value).filter(Boolean) || []
            )
          );
          if (uniqueValues.length > 0 && uniqueValues.length <= 10) {
            // Arbitrary limit for dropdown filters
            filters = uniqueValues.map((val) => ({
              text: String(val),
              value: val,
            }));
            onFilter = (value, record) =>
              String(record[key]?.value) === String(value);
          }
        }
        break;
      default:
        // Fallback for other or mixed types: treat as string for sorting and enable search
        sorter = (a, b) => {
          const valA = String(a[key]?.value || "");
          const valB = String(b[key]?.value || "");
          return valA.localeCompare(valB);
        };

        break;
    }

    return {
      sorter,
      filters,
      onFilter,
      ...searchProps,
    };
  };

  const defaultColumns = Object.keys(props.data?.[0] || {}).map((key) => {
    const sampleValue = props.data?.[0]?.[key];
    const features = getColumnTypeAndFeatures(key, sampleValue);

    return {
      title: key,
      dataIndex: [key, "value"],
      key: key,
      ...features,
    };
  });

  const columns = props.columns
    ? props.columns(defaultColumns)
    : defaultColumns;

  const dataSource = React.useMemo(() => {
    return !props.data
      ? []
      : props.data
          .filter((a) => {
            if (!searchText) return true; // No search text, show all
            const value = "" + a[searchedColumn]?.value;
            return (
              value &&
              String(value).toLowerCase().includes(searchText.toLowerCase())
            ); // Filter based on search text
          })
          .map((item, index) => ({
            key: index,
            ...item,
          }));
  }, [props.data, searchText]);
  return (
    <Drawer
      placement="bottom"
      height="80%"
      title={t("opendash:monitoring.data_sidebar.details")}
      {...props}
    >
      <Typography.Text type="secondary" style={{ marginBottom: "8px" }}>
        {t("opendash:ui.filter")}
      </Typography.Text>
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "8px",
          flexDirection: "row",
        }}
      >
        <Select
          style={{ width: "200px" }}
          value={searchedColumn}
          onChange={(value) => {
            setSearchedColumn(value);
            setSearchText(""); // Reset search text when changing column
          }}
          placeholder="Select a column to search"
          options={columns.map((col) => {
            return {
              label: col.title,
              value: col.key as string,
            };
          })}
        ></Select>
        <Input.Search
          style={{ flexGrow: 1 }}
          value={searchText}
          onChange={(e) => {
            const value = e.target.value;
            setSearchText(value || "");
          }}
        ></Input.Search>
      </div>
      {props.data && (
        <>
          <Typography.Text type="secondary" style={{ marginBottom: "8px" }}>
            {t("opendash:monitoring.datadetaildrawer.amount", {
              amount: dataSource.length,
            })}
          </Typography.Text>
          <Table
            style={{ height: "calc(100% - 64px)", overflowY: "auto" }}
            size="small"
            dataSource={dataSource}
            columns={columns}
            pagination={{
              position: ["topRight"],
              pageSize: 50,
              showSizeChanger: false,
            }}
          />
        </>
      )}
    </Drawer>
  );
};
